import { validFileTypes } from './fileConfig';
import {
  ValidatedEmailField,
  ValidatedField,
  ValidatedFieldDate,
  ValidatedZipCodeField,
} from './types/validation';

export const validateField = (field: ValidatedField): boolean => {
  let files = [] as File[];
  let fieldValidFileTypes = [] as string[];
  let maxFileSize = 10;
  if (field.validationType === 'FILE' && field.files !== undefined) {
    files = field.files.files;
    fieldValidFileTypes = field.files.validFileTypes as string[];
    maxFileSize = field.files.maxFileSize as number;
  }

  switch (field.validationType) {
    case 'PERSONALNUMBER':
      return personalNumberValidation(field);
    case 'NUMBER':
      return /^\d+$/.test(field.value as string);
    case 'ORGNUMBER':
      return orgNumberValidation(field);
    case 'DATE':
      return dateValidation(field as ValidatedFieldDate);
    case 'EXCELFILEDATE':
      return excelFileDateValidation(field as ValidatedField);
    case 'EMAIL':
      return emailValidation(field as ValidatedEmailField);
    case 'PHONENUMBER':
      return phoneNumberValidation(field);
    case 'FILE':
      return fileValidation(
        (files as File[]) ?? [],
        fieldValidFileTypes ?? validFileTypes,
        maxFileSize
      );
    case 'ZIPCODE':
      return isValidPostalCode(field as ValidatedZipCodeField);
    case 'TEXT':
    default:
      return textValidation(field);
  }
};

const phoneNumberValidation = (field: ValidatedField): boolean => {
  return !(
    (field.required || field.value !== '') &&
    !isPhoneNumber(field.value)
  );
};

const emailValidation = (field: ValidatedEmailField): boolean => {
  const isEmailValid = !field.required || isEmail(field.value);

  const isConfirmationValid = field.compareMail
    ? field.value.toLowerCase() === field.compareMail.toLowerCase()
    : true;

  return isEmailValid && isConfirmationValid;
};

const textValidation = (field: ValidatedField): boolean => {
  return !(field.required && stringIsNullOrEmpty(field.value as string));
};

const personalNumberValidation = (field: ValidatedField): boolean => {
  if (field.required && stringIsNullOrEmpty(field.value as string))
    return false;
  if ((field.value as string).length < 12) return false;

  return isPersonalNumber(field.value.replace(/-/g, ''));
};

const orgNumberValidation = (field: ValidatedField): boolean => {
  const regex = /^\d{6}-?\d{4}$/;

  if (field.required && (field.value === null || field.value === undefined)) {
    return false;
  } else if (field.required) {
    return regex.test((field.value as string).trim());
  } else {
    return true;
  }
};

export const isPersonalNumber = (personalNumber: string) =>
  personalNumber.length === 12 &&
  isValidLuhnAlgorithm(personalNumber) &&
  isValidPersonalNumberDate(personalNumber);

const isValidLuhnAlgorithm = (personalNumber: string) => {
  const personalNumberArray = personalNumber
    .replace(/\D/g, '')
    .split('')
    .reverse()
    .slice(0, 10);

  var sum = personalNumberArray
    .map((n) => Number(n))
    .reduce((previous, current, index) => {
      if (index % 2) current *= 2;
      if (current > 9) current -= 9;
      return previous + current;
    });

  return sum % 10 === 0;
};

export const isEmail = (email: string) => {
  const regex = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  if (email === null) {
    return false;
  }
  if (typeof email === 'undefined') {
    return false;
  }
  if (!regex.test(email)) {
    return false;
  }
  return true;
};

export const isPhoneNumber = (phoneNumber: string) => {
  const regex = /^[+]?[0-9]{2}[\s]*[0-9 -]{4,}$/;
  if (phoneNumber === null) {
    return false;
  }
  if (typeof phoneNumber === 'undefined') {
    return false;
  }
  if (!regex.test(phoneNumber)) {
    return false;
  }

  return true;
};

export const isMobilePhoneNumber = (input: string) =>
  /^(?:\+46|0046|0)?7\d{8}$/.test(input.replace(/[\s-]/g, ''));

const isValidPersonalNumberDate = (personalNumber: string) => {
  var year = parseInt(personalNumber.substring(2, 4));
  var month = parseInt(personalNumber.substring(4, 6));
  var day = parseInt(personalNumber.substring(6, 8));

  return isValidDate(year, month, day);
};

const fileValidation = (
  files: File[],
  validFileTypes: string[],
  maxFileSize = 10
) => {
  if (files.length === 0) return false;
  let allFilesPassCondition = true;
  files.forEach((file) => {
    const fileSizeMb = file.size / 1024 / 1024;
    if (fileSizeMb > maxFileSize) {
      allFilesPassCondition = false;
      return;
    }
    if (validFileTypes !== undefined) {
      const fileNameExtension = file.name.split('.').pop();
      if (
        !validFileTypes.some(
          (validFileType) => validFileType === fileNameExtension
        )
      )
        allFilesPassCondition = false;
      return;
    }
  });
  if (allFilesPassCondition) {
    return true;
  } else {
    return false;
  }
};

const swedishPostalCodeRegex = /^[0-9]{5}$/;

const isValidPostalCode = (field: ValidatedZipCodeField) => {
  if (field.required) {
    if (field.isForeignAddress) {
      return field.value !== '';
    } else {
      return isPostalCode(field.value as string);
    }
  } else {
    return true;
  }
};

export const isPostalCode = (postalCode: string) =>
  swedishPostalCodeRegex.test(postalCode);

const dateValidation = (field: ValidatedFieldDate): boolean => {
  // Check if the field is required but has no value
  if (field.required && field.value.length === 0) return false;

  const splitValue = field.value.split('-');
  const year = parseInt(splitValue[0].substring(2, 4));
  const month = parseInt(splitValue[1]);
  const day = parseInt(splitValue[2]);

  // Validate the basic date format
  let valid = isValidDate(year, month, day);

  if (valid) {
    const date = Date.parse(field.value);

    let compareValid = true; // To keep track of both comparisons being valid

    // Validate first comparison (compareDate and compareType)
    if (field.compareType && field.compareDate) {
      const compareDate = Date.parse(field.compareDate);
      if (field.compareType === 'GREATER_THAN') {
        compareValid = date >= compareDate;
      } else if (field.compareType === 'LESS_THAN') {
        compareValid = date <= compareDate;
      } else {
        compareValid = false; // Invalid compareType
      }
    }

    // Validate second comparison (compareDateTwo and compareTypeTwo)
    if (compareValid && field.compareTypeTwo && field.compareDateTwo) {
      const compareDateTwo = Date.parse(field.compareDateTwo);
      if (field.compareTypeTwo === 'GREATER_THAN') {
        compareValid = date >= compareDateTwo;
      } else if (field.compareTypeTwo === 'LESS_THAN') {
        compareValid = date <= compareDateTwo;
      } else {
        compareValid = false; // Invalid compareTypeTwo
      }
    }

    // The field is valid only if both compare checks (if present) are valid
    valid = compareValid;
  }

  return valid;
};

const isValidDate = (year: number, month: number, day: number) => {
  // Check year and month values
  if (year < 0 || year > 99 || month < 0 || month > 12) return false;

  let daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Adjust for leap years
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
    daysInMonth[1] = 29;
  }

  // Check that day is within range
  let dayIsValid = day > 0 && day <= daysInMonth[month - 1];

  // If day is outside range, check if it's +60 for samordningsnummer
  if (!dayIsValid) {
    dayIsValid = day > 60 && day <= daysInMonth[month - 1] + 60;
  }

  return dayIsValid;
};

const excelFileDateValidation = (field: ValidatedField): boolean => {
  if (!field.required && stringIsNullOrEmpty(field.value as string))
    return true;
  const dateString = field.value as string;
  const numbersOnlyDateString = dateString.replace(/[^0-9]/g, '');
  const patterns = [/^\d{6}$/, /^\d{8}$/];

  return patterns.some((pattern) => pattern.test(numbersOnlyDateString));
};

const stringIsNullOrEmpty = (text: string) =>
  text === null || text === undefined || text.length === 0;
